import { Directive, effect, ElementRef, inject, input, output } from '@angular/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({
  async: true,
  gfm: true,
  breaks: true
});

@Directive({
  selector: '[appMdAnimated]',
  standalone: true
})
export class MdAnimatedDirective {
  #host = inject(ElementRef<HTMLElement>).nativeElement;

  readonly markdown = input<string>('');
  readonly typewriter = input<boolean>(false);
  readonly speed = input<number>(40);
  readonly typingCompleted = output<void>();

  constructor() {
    effect(() => {
      const markdown = this.markdown();
      const typewriter = this.typewriter();
      const speed = this.speed();

      this.#render(markdown, typewriter, speed);
    });
  }

  async #render(markdown: string, typewriter: boolean, speed: number) {
    if (markdown == null) return;

    this.#host.innerHTML = '';

    const parsed = await marked.parse(markdown);
    const cleanHtml = DOMPurify.sanitize(parsed);

    if (typewriter) {
      await this.#startTyping(cleanHtml, speed);
    } else {
      this.#host.innerHTML = cleanHtml;
    }

    this.typingCompleted.emit();
  }

  async #startTyping(html: string, speed: number) {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    await this.#typeNodeRecursive(temp, this.#host, speed);
  }

  async #typeNodeRecursive(sourceNode: Node, targetContainer: HTMLElement, speed: number) {
    for (const child of Array.from(sourceNode.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const fullText = child.textContent || '';

        const textNode = document.createTextNode('');
        targetContainer.appendChild(textNode);

        for (let i = 0; i <= fullText.length; i++) {
          textNode.textContent = fullText.slice(0, i);
          await new Promise((r) => setTimeout(r, speed));
        }

        textNode.textContent = fullText;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const sourceEl = <HTMLElement>child;
        const el = document.createElement(sourceEl.tagName.toLowerCase());

        for (const attr of Array.from(sourceEl.attributes)) {
          el.setAttribute(attr.name, attr.value);
        }

        targetContainer.appendChild(el);

        await this.#typeNodeRecursive(sourceEl, el, speed);
      }
    }
  }
}
